import saveAs from "file-saver";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import {
  FileValidationParams,
  validateFileHeaders,
} from "../utils/validateFileHeaders";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export interface OperatorPrefixType {
  id: string;
  prefixNumber: string;
  isDeleted: boolean;
  operatorId: number;
  countryId: number;
  createdDate: Date;
  historyTimestamp: Date;
  createdBy?: string;
  updatedBy?: string;
}
export interface OperatorPrefixHistory {
  id: string;
  snapshot: OperatorPrefixType & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
}
export interface OperatorPrefixHistoryDiff {
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
  snapshot: OperatorPrefixType & { timestamp: string };
  diff?: Record<string, string>;
}

export const exportOperatorPrefix = async (
  params: Params<OperatorPrefixType> = {}
): Promise<void> => {
  try {
    // Filter out empty values
    const filteredParams: Record<string, any> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        filteredParams[key] = value;
      }
    });

    const response = await axiosInstance.get("/operator-prefixes/csv", {
      params: filteredParams,
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = "OperatorPrefix.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error fetching OperatorPrefix:",
      error.response?.data?.message || error.message || error
    );
  }
};

export async function fetchOperatorPrefixes(
  params: Params<OperatorPrefixType>
): Promise<IPageable<OperatorPrefixType>> {
  try {
    // Remove fields from params that are empty (null, undefined, or empty string)
    const filteredParams: Record<string, any> = {};
    Object.entries(params || {}).forEach(([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        !(typeof value === "string" && value.trim() === "")
      ) {
        filteredParams[key] = value;
      }
    });

    const response = await axiosInstance.get("/operator-prefixes", {
      params: filteredParams,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching operator prefixes."
    );
  }
}

export async function fetchOperatorPrefix(
  id: OperatorPrefixType["id"]
): Promise<OperatorPrefixType> {
  try {
    const response = await axiosInstance.get(`/operator-prefixes/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting operator-prefix/${id}.`
    );
  }
}

export const addOperatorPrefixApi = async (
  data: Partial<OperatorPrefixType>
) => {
  try {
    const response = await axiosInstance.post(
      "/operator-prefixes/upsert",
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the operator prefix."
    );
  }
};

export const editOperatorPrefixApi = async (
  id: string,
  data: Partial<OperatorPrefixType>
) => {
  try {
    const response = await axiosInstance.post(
      `/operator-prefixes/upsert`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the operator prefix."
    );
  }
};

export const getOperatorPrefixById = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/operator-prefixes/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the operator prefix."
    );
  }
};

export const deleteOperatorPrefixApi = async (ids: string[]): Promise<void> => {
  try {
    await axiosInstance.delete(`/operator-prefixes`, { data: ids });
    console.log(`Operator prefix with ID ${ids} deleted successfully.`);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while deleting the operator prefix."
    );
  }
};

export interface OperatorUploadParams {
  mcc: string;
  mnc: string;
  prefixNumber: string;
  operatorName: string;
  countryName: string;
}

export async function uploadOperatorPrefixesExcel(
  file: File,
  uploadParams: OperatorUploadParams
): Promise<void> {
  const extension = file.name.split(".").pop()?.toLowerCase() || "xlsx";

  const validationParams: FileValidationParams = {
    alwaysRequired: [
      uploadParams.mcc,
      uploadParams.mnc,
      uploadParams.prefixNumber,
    ],
    optionalFields: [uploadParams.operatorName, uploadParams.countryName],
  };

  await validateFileHeaders(file, extension, validationParams);

  const formData = new FormData();
  formData.append("file", file);

  const endpoint = extension === "csv" ? "csv" : "excel";

  const params = {
    mccHeader: uploadParams.mcc,
    mncHeader: uploadParams.mnc,
    prefixNumberHeader: uploadParams.prefixNumber,
    operatorHeader: uploadParams.operatorName,
    countryHeader: uploadParams.countryName,
  };

  try {
    await axiosInstance.post(`/operator-prefixes/${endpoint}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      params,
    });
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while uploading the operator prefixes Excel."
    );
  }
}

export const useOperatorPrefixesQuery = (
  params: Params<OperatorPrefixType>
): UseQueryResult<IPageable<OperatorPrefixType>, Error> => {
  const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };

  const queryKey = ["operatorPrefixes", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchOperatorPrefixes(effectiveParams),
  });
};

export async function fetchOperatorPrefixHistory(
  id: OperatorPrefixType["id"],
  params: Params<OperatorPrefixHistory> = {}
): Promise<IPageable<OperatorPrefixType>> {
  try {
    const response = await axiosInstance.get(`/operator-prefixes/history`, {
      params: { mainId: id, sort: "historyTimestamp,desc", ...params },
    });
    const data = response.data;
    return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp,
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the Operator Prefix history for ID ${id}.`
    );
  }
}
